import { ApiOrganizationCatalogScopeEnum } from "@logick/core-api"
import { LgkNavMenuItem } from "@logick/ui/api"

import { ADMIN_MENU } from "@/features/admin"
import { UserRole } from "@/features/admin/users/models/user.model"
import { ANALYTICS_MENU } from "@/features/analytics"
import { CATALOG_MENU } from "@/features/catalog"
import { CRM_MENU } from "@/features/crm"
import { DASHBOARD_MENU } from "@/features/dashboard"
import { HELP_MENU } from "@/features/help"
import {SIGN_OUT_MENU} from "@/features/logout"
import { SALES_MENU } from "@/features/sales"
import { USER_ACCOUNT_MENU } from "./features/user-account"


export const APP_MENU: LgkNavMenuItem[] = [
  USER_ACCOUNT_MENU,
  DASHBOARD_MENU,
  SALES_MENU,
  CATALOG_MENU,
  CRM_MENU,
  ANALYTICS_MENU,
  ADMIN_MENU,
  {separator:true, separatorAtEnd:true, visible:false},
  //HELP_MENU
]

// Correspondance entre les routes du catalogue et le scope de catalogue de l'organisation
// (ApiOrganizationModel.catalogScope) qui conditionne leur visibilité pour un partenaire.
const CATALOG_SCOPE_BY_ROUTE: Record<string, ApiOrganizationCatalogScopeEnum> = {
  events: ApiOrganizationCatalogScopeEnum.Showtime,
  cinemas: ApiOrganizationCatalogScopeEnum.Voucher,
  bons: ApiOrganizationCatalogScopeEnum.MultiPartnerVoucher
}

function isInCatalogScope(routerLink: string | undefined, catalogScope: ApiOrganizationCatalogScopeEnum[]): boolean {
  const requiredScope = routerLink ? CATALOG_SCOPE_BY_ROUTE[routerLink] : undefined
  return requiredScope === undefined || catalogScope.includes(requiredScope)
}

// Route de catalogue par défaut vers laquelle rediriger un partenaire selon son catalogScope,
// par ordre de priorité (Événements > Cinémas > Bons d'achat).
export function getDefaultPartnerRoute(catalogScope: ApiOrganizationCatalogScopeEnum[]): string {
  const route = Object.entries(CATALOG_SCOPE_BY_ROUTE).find(([, scope]) => catalogScope.includes(scope))?.[0]
  return route ? `/catalog/${route}` : "/catalog"
}

export function getMenuForRole(role: UserRole | null, catalogScope: ApiOrganizationCatalogScopeEnum[] = []): LgkNavMenuItem[] {

  if (role == null) {
    return []
  }

  if (role === UserRole.PARTNER) {
    // Pour les partenaires : garder Mon Compte, Catalogue (Événements, Cinémas, Bons d'achat), Aide et Déconnexion
    // L'accès aux catalogues est ouvert à tous les partenaires : le contenu affiché est filtré
    // côté API en fonction du partenaire propriétaire, un partenaire ne voit donc que ses propres données.
    // De plus, seuls les produits correspondants au catalogScope de l'organisation sont affichés.
    const filteredCatalogMenu: LgkNavMenuItem = {
      ...CATALOG_MENU,
      items: CATALOG_MENU.items?.map(section => {
        if (section.label === "Événementiel") {
          return {
            ...section,
            items: section.items?.filter(item => isInCatalogScope(item.routerLink, catalogScope))
          }
        }
        if (section.label === "Avantages") {
          return {
            ...section,
            items: section.items?.filter(item => item.label === "Bons d'achat" && isInCatalogScope(item.routerLink, catalogScope))
          }
        }
        return undefined
      }).filter(Boolean).filter(section => (section as LgkNavMenuItem).items?.length) as LgkNavMenuItem[]
    }

    const filteredAnalyticsMenu: LgkNavMenuItem = {
      ...ANALYTICS_MENU,
      items: ANALYTICS_MENU.items?.map(section => {
        if (section.label === "Comptabilité") {
          return {
            ...section,
            items: section.items?.filter(item => item.routerLink === "partners")
          }
        }
        return undefined
      }).filter(Boolean) as LgkNavMenuItem[]
    }

    const filteredAdminMenu: LgkNavMenuItem = {
      ...ADMIN_MENU,
      items: ADMIN_MENU.items?.map(section => {
        if (section.label === "Paramètres") {
          return {
            ...section,
            items: section.items?.filter(item => item.routerLink === "control-config")
          }
        }
        return undefined
      }).filter(Boolean) as LgkNavMenuItem[]
    }

    return [
      USER_ACCOUNT_MENU,
      filteredCatalogMenu,
      filteredAnalyticsMenu,
      filteredAdminMenu,
      {separator: true, separatorAtEnd: true, visible: false},
      //HELP_MENU
    ]
  }

  if (role === UserRole.ACCOUNTANT) {
    const filteredAnalyticsMenu: LgkNavMenuItem = {
      ...ANALYTICS_MENU,
      items: ANALYTICS_MENU.items?.map(section => {
        if (section.label === "Comptabilité") {
          return {
            ...section,
            items: section.items?.filter(item => item.routerLink === "sales-reports" || item.routerLink === "payment-settlements")
          }
        }
        return undefined
      }).filter(Boolean) as LgkNavMenuItem[]
    }

    return [
      USER_ACCOUNT_MENU,
      filteredAnalyticsMenu,
      {separator: true, separatorAtEnd: true, visible: false},
      //HELP_MENU
    ]
  }


  // Pour les admins ou autres : menu complet sauf "Bordereau de vente" (réservé aux partenaires)
  const filteredAnalyticsMenuForOthers: LgkNavMenuItem = {
    ...ANALYTICS_MENU,
    items: ANALYTICS_MENU.items?.map(section => {
      if (section.label === "Comptabilité") {
        return {
          ...section,
          items: section.items?.filter(item => item.routerLink !== "partners")
        }
      }
      return section
    })
  }

  return APP_MENU.map(menu => menu === ANALYTICS_MENU ? filteredAnalyticsMenuForOthers : menu)
}
